import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// Timesheets → Holidays (Kloepfer K6-13, 2026-10-07). Larry: "as a company,
// we pay them on a holiday… it just shows holiday, and then it automatically
// gives them 8 hours". The office keeps a list per year here - one click for
// the US holidays, or add and remove by hand. Every change answers with the
// year's list, and closing the pop-up after a change redraws just the week
// or month grid (no page reload) so the green days show at once.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

export default class extends Controller {
  static targets = ["modal", "year", "list", "date", "name", "hours", "error", "usButton", "copyButton", "usLink", "trashIcon"]
  static values = { url: String, usUrl: String, text: Object, year: Number, inline: Boolean }

  connect() {
    this.year = this.yearValue || new Date().getFullYear()
    this.changed = false
    this.removing = null
    this._onKey = (e) => {
      if (e.key === "Escape" && this.hasModalTarget && !this.modalTarget.classList.contains("hidden")) this.close()
    }
    document.addEventListener("keydown", this._onKey)
    // Inline (Company Settings → Timesheets → Holidays): the list is the page.
    if (this.inlineValue) this.open()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKey)
  }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`%{${k}}`, v)
    return s
  }

  async open() {
    this.modalTarget.classList.remove("hidden")
    this.changed = false
    this._error(null)
    await this.load()
  }

  close() {
    if (this.inlineValue || this.modalTarget.classList.contains("hidden")) return
    this.modalTarget.classList.add("hidden")
    if (this.changed) this.refreshGrid()
    this.changed = false
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  prevYear() {
    this.year -= 1
    this.load()
  }

  nextYear() {
    this.year += 1
    this.load()
  }

  async load() {
    this.removing = null
    this.copyFrom = null
    this._drawYear()
    this.listTarget.innerHTML = `<li class="py-2 text-[13px] text-gray-400">${esc(this.t("loading"))}</li>`
    const data = await this._request("GET", `${this.urlValue}?year=${this.year}`)
    if (data) this._take(data)
  }

  async add() {
    const date = this.dateTarget.value.trim()
    const name = this.nameTarget.value.trim()
    if (!date) return this._error(this.t("errors.date"))
    if (!name) return this._error(this.t("errors.name"))
    this._error(null)
    const data = await this._request("POST", this.urlValue, { date, name, hours: this.hoursTarget.value })
    if (!data) return
    this.changed = true
    this.dateTarget._flatpickr ? this.dateTarget._flatpickr.clear() : (this.dateTarget.value = "")
    this.nameTarget.value = ""
    this.hoursTarget.value = this.hoursTarget.defaultValue
    this._take(data)
    showToast(this.t("saved"))
  }

  async addUs() {
    this._error(null)
    const data = await this._request("POST", this.usUrlValue, { year: this.year })
    if (!data) return
    if (data.added > 0) this.changed = true
    this._take(data)
    showToast(data.added > 0 ? this.t("added_us", { count: data.added }) : this.t("added_none", { year: data.year }))
  }

  // Last year's own list on this year's dates (Kloepfer K3-89).
  async copy() {
    this._error(null)
    const from = this.copyFrom?.year
    const data = await this._request("POST", `${this.urlValue}/copy`, { year: this.year })
    if (!data) return
    if (data.added > 0) this.changed = true
    this._take(data)
    showToast(this.t(data.added === 1 ? "copied_one" : "copied", { count: data.added, year: data.copied_from || from }))
  }

  askRemove(e) {
    this.removing = e.currentTarget.closest("[data-holiday-id]").dataset.holidayId
    this._drawList()
  }

  keep() {
    this.removing = null
    this._drawList()
  }

  async remove(e) {
    const id = e.currentTarget.closest("[data-holiday-id]").dataset.holidayId
    const data = await this._request("DELETE", `${this.urlValue}/${encodeURIComponent(id)}`)
    if (!data) return
    this.changed = true
    this.removing = null
    this._take(data)
    showToast(this.t("removed"))
  }

  // Swap in the week or month as the server now draws it.
  async refreshGrid() {
    const grid = document.querySelector("[data-timesheets-grid]")
    if (!grid) return
    try {
      const res = await fetch(window.location.href, { headers: { Accept: "text/html" }, credentials: "same-origin" })
      if (!res.ok) throw new Error(String(res.status))
      const doc = new DOMParser().parseFromString(await res.text(), "text/html")
      const fresh = doc.querySelector("[data-timesheets-grid]")
      if (!fresh) return
      grid.innerHTML = fresh.innerHTML
      this.dispatch("changed")
    } catch (_e) {
      showToast(this.t("failed"), "error")
    }
  }

  // ── Drawing ─────────────────────────────────────────────────────────────
  _take(data) {
    if (data.year) this.year = data.year
    this.holidays = data.holidays || []
    this.copyFrom = data.copy_from || null
    this._drawYear()
    this._drawList()
  }

  _drawYear() {
    this.yearTarget.textContent = this.year
    this.usButtonTarget.textContent = this.t("add_us", { year: this.year })
    // Until the list is in, the year shows the US button alone.
    const copy = this.copyFrom
    this.usButtonTarget.classList.toggle("hidden", !!copy)
    if (!this.hasCopyButtonTarget) return
    this.copyButtonTarget.classList.toggle("hidden", !copy)
    this.usLinkTarget.classList.toggle("hidden", !copy)
    if (!copy) return
    this.copyButtonTarget.textContent = this.t(copy.count === 1 ? "copy_from_one" : "copy_from", { count: copy.count, year: copy.year })
    this.usLinkTarget.textContent = this.t("or_add_us")
  }

  _drawList() {
    const list = this.holidays || []
    if (!list.length) {
      this.listTarget.innerHTML = `<li class="py-3 text-[13px] text-gray-500">${esc(this.t("empty", { year: this.year }))}</li>`
      return
    }
    this.listTarget.innerHTML = list.map((h) => {
      const asking = String(h.id) === String(this.removing)
      const actions = asking
        ? `<span class="text-[13px] text-gray-700">${esc(this.t("remove_confirm", { name: h.name }))}</span>
           <button type="button" data-action="click->time-holidays#remove" class="px-2.5 py-1 rounded-md text-[13px] font-semibold text-white bg-red-600 hover:bg-red-700 cursor-pointer">${esc(this.t("remove"))}</button>
           <button type="button" data-action="click->time-holidays#keep" class="px-2.5 py-1 rounded-md text-[13px] text-gray-600 hover:bg-gray-100 cursor-pointer">${esc(this.t("keep"))}</button>`
        : `<button type="button" data-action="click->time-holidays#askRemove" class="p-1.5 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer"
                   title="${esc(this.t("remove"))}" aria-label="${esc(this.t("remove"))} ${esc(h.name)}">
             ${this.hasTrashIconTarget ? this.trashIconTarget.innerHTML : "×"}
           </button>`
      // data-search-row: the word search over the list (list_search_controller.js, K8-22) finds it by date and name.
      return `<li class="flex items-center gap-3 py-2" data-holiday-id="${esc(h.id)}" data-search-row data-search-text="${esc(`${h.label} ${h.name}`)}">
        <span class="inline-block w-2 h-2 rounded-full bg-green-500 shrink-0" aria-hidden="true"></span>
        <span class="w-24 shrink-0 text-[13px] text-gray-700 tabular-nums">${esc(h.label)}</span>
        <span class="flex-1 min-w-0 truncate text-[13px] font-medium text-gray-900">${esc(h.name)}</span>
        <span class="text-[13px] text-green-700 tabular-nums">${esc(this.t("hours_short", { hours: h.hours }))}</span>
        <span class="flex items-center gap-1">${actions}</span>
      </li>`
    }).join("")
  }

  _error(message) {
    this.errorTarget.textContent = message || ""
    this.errorTarget.classList.toggle("hidden", !message)
  }

  async _request(method, url, body) {
    try {
      const res = await fetch(url, {
        method,
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: method === "GET" ? undefined : JSON.stringify(body || {})
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        this._error(data.error || this.t("failed"))
        return null
      }
      return data
    } catch (_e) {
      this._error(this.t("failed"))
      return null
    }
  }
}
